import { Component, RefObject, createRef } from 'react';

import cx from 'classnames';
import { format } from 'date-fns';

import { Button, Img } from 'components/base';
import { ShareButton, ToastCloseIcon, Pagination } from 'styled';

import Language from 'constants/Language';

import { ArticleUserStory as IArticleUserStory } from 'types';

interface Props {
  articleUserStories: IArticleUserStory[];
}

interface StoryRefs {
  [storyId: string]: RefObject<HTMLParagraphElement>;
}

interface ShowReadMoreButtonStatus {
  [storyId: string]: boolean;
}

interface State {
  currentPaginationNumber: number;
  totalPaginationCount: number;
  currentPageStories: IArticleUserStory[];
  activeStory: string | null;
  storyRefs: StoryRefs;
  showReadMoreButtonStatus: {
    [storyId: string]: boolean;
  };
  activeShareDropdown: string | null;
  currentUrl: string;
  copied: boolean;
}

const USER_STORY_PAGINATION_PER_PAGE = 3;

class ArticleUserStory extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const currentPageStories = this.getCurrentPageStories(1);
    let currentUrl = '';

    if (process.browser) {
      currentUrl = window.location.href;
    }

    this.state = {
      currentPaginationNumber: 1,
      totalPaginationCount: Math.ceil(
        props.articleUserStories.length / USER_STORY_PAGINATION_PER_PAGE
      ),
      currentPageStories,
      activeStory: null,
      storyRefs: this.setStoryRefs(currentPageStories),
      showReadMoreButtonStatus: {},
      activeShareDropdown: null,
      currentUrl: currentUrl,
      copied: false,
    };
  }

  componentDidMount() {
    this.setShowReadMoreButtonStatus();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      currentPaginationNumber,
      currentPageStories,
      storyRefs,
    } = this.state;

    if (prevState.currentPaginationNumber !== currentPaginationNumber) {
      const currentPageStories = this.getCurrentPageStories(
        currentPaginationNumber
      );

      this.setState({
        currentPageStories: currentPageStories,
        storyRefs: this.setStoryRefs(currentPageStories),
      });

      setTimeout(() => this.setShowReadMoreButtonStatus(), 0);
    }

    // Scroll to first story of page if user changes page number
    if (
      !!this.props.articleUserStories.length &&
      prevState.currentPageStories[0]?.id !== currentPageStories[0]?.id
    ) {
      const firstStoryOnPage = storyRefs[currentPageStories[0].id];

      if (firstStoryOnPage.current && firstStoryOnPage.current.parentElement) {
        window.scroll({
          top: firstStoryOnPage.current.parentElement.offsetTop,
        });
      }
    }
  }

  setStoryRefs = (stories: IArticleUserStory[]) => {
    return stories.reduce((storyRefs: StoryRefs, story) => {
      storyRefs[story.id] = createRef();
      return storyRefs;
    }, {});
  };

  getCurrentPageStories = (
    currentPaginationNumber: number
  ): IArticleUserStory[] => {
    return this.props.articleUserStories.slice(
      currentPaginationNumber * USER_STORY_PAGINATION_PER_PAGE -
        USER_STORY_PAGINATION_PER_PAGE,
      currentPaginationNumber * USER_STORY_PAGINATION_PER_PAGE
    );
  };

  handlePageChange = (pageNumber: number) => {
    this.setState({ currentPaginationNumber: pageNumber + 1 });
  };

  handleReadMoreClick = (storyId: string) => {
    this.setState((state) => ({
      activeStory: state.activeStory === storyId ? null : storyId,
    }));
  };

  setShowReadMoreButtonStatus = () => {
    const showReadMoreButtonStatus = this.state.currentPageStories.reduce(
      (showReadMoreButtonStatus: ShowReadMoreButtonStatus, story) => {
        showReadMoreButtonStatus[story.id] = this.storyIsOverflown(story.id);

        return showReadMoreButtonStatus;
      },
      {}
    );

    this.setState({ showReadMoreButtonStatus });
  };

  storyIsOverflown = (id: string) => {
    const ref = this.state.storyRefs[id];

    if (ref.current) {
      return ref.current.scrollHeight > ref.current.clientHeight;
    }

    return false;
  };

  toggleShareDropdown = (id: string) => {
    this.setState((state) => {
      const activeShareDropdown = state.activeShareDropdown;

      if (activeShareDropdown === id) {
        return {
          ...state,
          activeShareDropdown: null,
        };
      }

      return {
        ...state,
        activeShareDropdown: id,
      };
    });
  };

  handleCopyCurrentUrl = (containerRef: RefObject<HTMLDivElement>) => {
    if (!containerRef.current) return;

    const { currentUrl } = this.state;

    const dummy = document.createElement('input');

    /*
     * Append dummy input element within ShareButton to maintain focus
     * at correct position in DOM tree after copying the url
     */
    containerRef.current.appendChild(dummy);
    dummy.value = currentUrl;
    dummy.select();
    document.execCommand('copy');
    containerRef.current.removeChild(dummy);

    this.setState({ copied: true });
    setTimeout(this.handleCloseCopyMsg, 3000);
  };

  handleCloseCopyMsg = () => {
    this.setState({ copied: false });
  };

  render() {
    const {
      totalPaginationCount,
      currentPageStories,
      activeStory,
      storyRefs,
      showReadMoreButtonStatus,
      currentUrl,
      activeShareDropdown,
      copied,
    } = this.state;

    if (!this.props.articleUserStories.length) {
      return null;
    }

    return (
      <div className="ArticleUserStory bg-color-white pb2_5 md:pb4_5">
        {currentPageStories.map((userStory) => {
          const id: string = userStory.id;
          const storyIsActive = activeStory === id;
          const toggleShareDropdown = () => this.toggleShareDropdown(id);

          return (
            <div
              key={id}
              className="article-content-padding border-top-gray-lighter w100 py3"
            >
              <div className="md:col-10 article-content-width">
                <span className="block text-lg color-blue">
                  {userStory.title}
                </span>
                <span className="block mt_75 text-xs">
                  {Language.t('ArticleUserStory.dateAndName', {
                    date: format(new Date(userStory.date), 'MMMM d, y'),
                    name: userStory.name,
                  })}
                </span>
                <p
                  ref={storyRefs[id] as RefObject<HTMLParagraphElement>}
                  className={cx(
                    'ArticleUserStory__story text-secondary-sm mt3 transition',
                    {
                      'ArticleUserStory__story--hide-overflow': !storyIsActive,
                    }
                  )}
                >
                  {userStory.story}
                </p>
                {showReadMoreButtonStatus[id] && (
                  <Button
                    ariaLabel={
                      storyIsActive
                        ? Language.t('ArticleUserStory.readLess')
                        : Language.t('ArticleUserStory.readMore')
                    }
                    containerClassName="mt1_5"
                    className="ArticleUserStory__read-more-button"
                    onClick={() => this.handleReadMoreClick(id)}
                  >
                    <span className="link-border-bottom-blue color-blue text-xs">
                      {storyIsActive
                        ? Language.t('ArticleUserStory.readLess')
                        : Language.t('ArticleUserStory.readMore')}
                    </span>
                  </Button>
                )}
                <div className="mt3 flex items-start">
                  {/* TO_DO: Add link button back in when like function has implemented.*/}
                  {/* <Button
                    ariaLabel=""
                    containerClassName=" mr2_25"
                    onClick={() => {}}
                  >
                    <div className="flex items-center">
                      <Img
                        alt={Language.t('ArticleUserStory.likeIcon')}
                        className="ArticleUserStory__like-icon mr_75"
                        src="/images/like-icon-sm.svg"
                      />
                      <span className="text-xs color-blue">
                        {Language.t('ArticleUserStory.likes', {
                          number: userStory.likes,
                        })}
                      </span>
                    </div>
                  </Button> */}
                  <ShareButton
                    hideDropdown={activeShareDropdown !== id}
                    toggleShareDropdown={toggleShareDropdown}
                    currentUrl={currentUrl}
                    handleCopyCurrentUrl={this.handleCopyCurrentUrl}
                  />
                </div>
              </div>
            </div>
          );
        })}
        <div className="w100 flex justify-center md:col-10 article-content-width">
          <Pagination
            pageCount={totalPaginationCount}
            pageRangeDisplayed={5}
            marginPagesDisplayed={0}
            onPageChange={(currentPage: { selected: number }) =>
              this.handlePageChange(currentPage.selected)
            }
            activeLinkBgColor="blue"
          />
        </div>
        <div
          className={cx(
            'ArticleUserStory__copy-msg-container fixed z-overlay bg-color-blue p1_25 m3 b0 flex items-center transition',
            {
              'ArticleUserStory__copy-msg-container--inactive': !copied,
            }
          )}
          aria-hidden={!copied}
        >
          <Img
            alt={Language.t('ArticleUserStory.checkIcon')}
            src="/images/check-icon.svg"
            className="ArticleUserStory__check-icon mr1_5"
          />
          <span className="color-white text-xs" role="alert">
            {Language.t('ArticleUserStory.copySuccessMsg')}
          </span>
          <Img
            alt=""
            src="/images/bar.svg"
            className="ArticleUserStory__bar-icon mx1_5"
          />
          <Button
            ariaLabel={Language.t(
              'ArticleUserStory.dismissCopySuccessAriaLabel'
            )}
            onClick={this.handleCloseCopyMsg}
            tabIndex={!copied ? -1 : undefined}
          >
            <ToastCloseIcon className="ArticleUserStory__close-icon" />
          </Button>
        </div>
      </div>
    );
  }
}

export default ArticleUserStory;
